import React from 'react';
import { 
  X, 
  Check, 
  Globe,
  Code,
  Cpu,
  Coins,
  ArrowLeftRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useThemeStore } from '../../stores/theme.store';

interface ProviderTemplate {
  id: string;
  name: string;
  type: string;
  category: string;
  version: string;
  status: string;
  description?: string;
  documentation?: string;
  documentationUrl?: string;
  setupGuide?: string;
  codeExamples?: any[];
  useCases?: any[];
  changelog?: any[];
  faq?: any[];
  knownLimitations?: string[];
  environmentRequirements?: any[];
  config?: {
    baseUrl?: string;
    authConfig?: {
      type: string;
      headerName?: string;
    };
    credentialSchema?: any[];
    operations?: any[];
    webhookConfig?: { enabled: boolean };
  };
}

interface TemplateComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: ProviderTemplate[];
}

const providerTypeIcons: Record<string, React.ElementType> = {
  REST: Globe,
  REST_API: Globe,
  GRAPHQL: Code,
  EVM: Cpu,
  BLOCKCHAIN_EVM: Cpu,
  SOLANA: Coins,
  BLOCKCHAIN_SOLANA: Coins,
  DEX: ArrowLeftRight,
  DEX_GENERIC: ArrowLeftRight,
};

export function TemplateComparisonModal({ isOpen, onClose, templates }: TemplateComparisonModalProps) {
  const { isDarkMode } = useThemeStore();

  const colors = isDarkMode ? {
    modalBg: 'bg-gray-900',
    headerBorder: 'border-gray-700',
    titleColor: 'text-white',
    subtitleColor: 'text-gray-400',
    closeButtonColor: 'text-gray-400 hover:text-gray-200 hover:bg-gray-800',
    tableBg: 'bg-gray-800',
    tableBorder: 'border-gray-700',
    rowBg: 'bg-gray-900',
    rowAltBg: 'bg-gray-800/50',
    cellText: 'text-gray-300',
    headerText: 'text-gray-400',
    labelText: 'text-white',
    checkIcon: 'text-green-400',
    crossIcon: 'text-red-400',
    minusIcon: 'text-gray-600',
    footerBg: 'bg-gray-800',
    footerBorder: 'border-gray-700',
  } : {
    modalBg: 'bg-white',
    headerBorder: 'border-gray-200',
    titleColor: 'text-gray-900',
    subtitleColor: 'text-gray-500',
    closeButtonColor: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
    tableBg: 'bg-gray-50',
    tableBorder: 'border-gray-200',
    rowBg: 'bg-white',
    rowAltBg: 'bg-gray-50',
    cellText: 'text-gray-700',
    headerText: 'text-gray-500',
    labelText: 'text-gray-900',
    checkIcon: 'text-green-600',
    crossIcon: 'text-red-600',
    minusIcon: 'text-gray-300',
    footerBg: 'bg-gray-50',
    footerBorder: 'border-gray-200',
  };

  const getTypeIcon = (type: string) => {
    const Icon = providerTypeIcons[type] || Code;
    return <Icon className="w-5 h-5" />;
  };

  const renderValue = (value: any, type: 'boolean' | 'number' | 'text' | 'array') => {
    if (type === 'boolean') {
      return value ? (
        <Check className={`w-5 h-5 ${colors.checkIcon}`} />
      ) : (
        <X className={`w-5 h-5 ${colors.crossIcon}`} />
      );
    }
    if (type === 'number') {
      return <span className={colors.cellText}>{value || 0}</span>;
    }
    if (type === 'array') {
      return <span className={colors.cellText}>{value?.length || 0}</span>;
    }
    return <span className={colors.cellText}>{value || '-'}</span>;
  };

  const comparisonRows = [
    { label: 'Tipo', key: 'type', type: 'text' as const },
    { label: 'Categoria', key: 'category', type: 'text' as const },
    { label: 'Versão', key: 'version', type: 'text' as const },
    { label: 'Status', key: 'status', type: 'text' as const },
    { label: 'Documentação', key: 'documentation', type: 'boolean' as const, getValue: (t: ProviderTemplate) => !!t.documentation },
    { label: 'Guia de Setup', key: 'setupGuide', type: 'boolean' as const, getValue: (t: ProviderTemplate) => !!t.setupGuide },
    { label: 'Exemplos de Código', key: 'codeExamples', type: 'array' as const, getValue: (t: ProviderTemplate) => t.codeExamples },
    { label: 'Casos de Uso', key: 'useCases', type: 'array' as const, getValue: (t: ProviderTemplate) => t.useCases },
    { label: 'FAQ', key: 'faq', type: 'array' as const, getValue: (t: ProviderTemplate) => t.faq },
    { label: 'Changelog', key: 'changelog', type: 'array' as const, getValue: (t: ProviderTemplate) => t.changelog },
    { label: 'Limitações Conhecidas', key: 'knownLimitations', type: 'array' as const, getValue: (t: ProviderTemplate) => t.knownLimitations },
    { label: 'Tipo de Autenticação', key: 'authType', type: 'text' as const, getValue: (t: ProviderTemplate) => t.config?.authConfig?.type || '-' },
    { label: 'Operações', key: 'operations', type: 'array' as const, getValue: (t: ProviderTemplate) => t.config?.operations },
    { label: 'Campos de Credencial', key: 'credentials', type: 'array' as const, getValue: (t: ProviderTemplate) => t.config?.credentialSchema },
    { label: 'Webhooks', key: 'webhooks', type: 'boolean' as const, getValue: (t: ProviderTemplate) => t.config?.webhookConfig?.enabled },
  ];

  if (templates.length < 2) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-50 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className={`w-full max-w-4xl ${colors.modalBg} rounded-xl shadow-2xl pointer-events-auto flex flex-col max-h-[90vh]`}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${colors.headerBorder} flex-shrink-0`}>
                <div>
                  <h2 className={`text-xl font-semibold ${colors.titleColor}`}>
                    Comparação de Templates
                  </h2>
                  <p className={`text-sm ${colors.subtitleColor}`}>
                    Compare as características dos templates selecionados
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg ${colors.closeButtonColor} transition-colors`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className={`${colors.tableBg} rounded-lg border ${colors.tableBorder} overflow-hidden`}>
                  <table className="w-full">
                    <thead>
                      <tr className={colors.rowAltBg}>
                        <th className={`p-4 text-left text-sm font-medium ${colors.headerText} w-1/3`}>
                          Característica
                        </th>
                        {templates.map((template) => (
                          <th key={template.id} className={`p-4 text-center ${colors.headerText}`}>
                            <div className="flex flex-col items-center gap-2">
                              <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                {getTypeIcon(template.type)}
                              </div>
                              <span className={`font-semibold ${colors.labelText}`}>{template.name}</span>
                              <Badge className="bg-gray-500/20 text-gray-400 text-xs">v{template.version}</Badge>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonRows.map((row, index) => (
                        <tr key={row.key} className={index % 2 === 0 ? colors.rowBg : colors.rowAltBg}>
                          <td className={`p-4 text-sm font-medium ${colors.labelText}`}>
                            {row.label}
                          </td>
                          {templates.map((template) => {
                            const value = row.getValue 
                              ? row.getValue(template) 
                              : (template as any)[row.key];
                            return (
                              <td key={template.id} className="p-4 text-center">
                                {renderValue(value, row.type)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div className={`flex-shrink-0 px-6 py-4 border-t ${colors.footerBorder} flex items-center justify-end ${colors.footerBg} rounded-b-xl`}>
                <Button variant="secondary" onClick={onClose}>
                  Fechar
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

export default TemplateComparisonModal;
